import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from 'react-query'
import { useSelector } from 'react-redux'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { produce } from 'immer'
import isEqual from 'lodash/isEqual'

import { VisitureProductsQueryParams } from 'lib/types'
import { RootState } from 'lib/core/data/reducer'
import { CATALOG_PAGE, CATALOG_SORT, MAX_PAGE_SIZE } from 'lib/core/resources/constants'
import { useCoreContext } from 'lib/core/hooks'
import { getProductAttributeList } from 'lib/core/data/common/productAttributeList/selectors'
import { getPageSizeDefault, getPageSizesAvailable, getCatalogSortDefault } from 'lib/core/data/common/storeConfig/selectors'
import { FilterItemRenderer, useCategoryList, normalizeProduct, ClearDraftFiltersHandler } from 'lib/modules/catalog'

import {
  pathToObj,
  objToPath,
  getFiltersQuery,
  getFiltersParams,
  getFiltersCollection,
  getFiltersMap,
  getFilterOptions,
  getCurrentSort,
  normalizePageInfo,
  getCurrentFilters
} from './utils'

import { fetchAttributeSettings } from './attributeSettings'
import { triggerItemList, triggerSearch } from './gtm'
import { useInfiniteScrollAddon } from './infiniteScrollAddon'
import { Filters, ChangeFilterHandler, RemoveFilterHandler, SetFilter } from './types'

export * from './types'

interface Params {
  categoryId?: number
  enableGTM?: boolean
  withProducts?: boolean
  withLayeredNav?: boolean
  withInfiniteScroll?: boolean
  filterElementRenderer?: FilterItemRenderer
  draftMode?: boolean
  onFilterChange?: () => void
}

export const useProductListPage = (params?: Params) => {
  const {
    categoryId,
    enableGTM,
    withLayeredNav,
    withProducts,
    withInfiniteScroll,
    draftMode,
    onFilterChange
  } = params ?? {}

  const { magentoClient, customConfiguration } = useCoreContext()
  const { query: searchQuery } = useParams<{ query: string }>()
  const categoryList = useCategoryList()
  const history = useHistory()
  const location = useLocation()
  const storeConfig = useSelector((state: RootState) => state.common.storeConfig.payload)
  const filtersAreAnd = Boolean(customConfiguration?.filtersAreAnd)
  const filterChangeHandler = useRef(onFilterChange)
  // Store Config
  const availablePageSizes = useSelector(getPageSizesAvailable)
  const defaultPageSize = useSelector(getPageSizeDefault)
  const catalogSortDefault = useSelector(getCatalogSortDefault)
  // Common
  const productAttrList = useSelector(getProductAttributeList)
  const filterItemRenderer = useRef<FilterItemRenderer | undefined>()
  filterItemRenderer.current = params?.filterElementRenderer

  /**
   * setFilters
   */
  const setFilterHandler: SetFilter = useCallback((filters: Filters, replace = false) => {
    const filtersQuery = getFiltersQuery(filters)
    const path = `${window.location.pathname}#${objToPath(filtersQuery)}`
    filterChangeHandler.current?.()
    history[replace ? 'replace' : 'push'](path)
  }, [history])

  /**
   * Filters
   */
  const filters: Filters = useMemo<Filters>(() => {
    const _filters = pathToObj(location.hash.substring(1))
    if (!_filters[CATALOG_PAGE.PAGE_SIZE]) {
      _filters[CATALOG_PAGE.PAGE_SIZE] = [`${defaultPageSize}`]
    }

    if (defaultPageSize !== MAX_PAGE_SIZE && !_filters[CATALOG_PAGE.PAGE_SIZE]) {
      _filters[CATALOG_PAGE.PAGE_SIZE] = [defaultPageSize.toString()]
    }
    return _filters
  }, [
    defaultPageSize,
    location.hash
  ])

  /**
   * Draft Filter Handler
   */
  const [draftFilters, setDraftFilters] = useState<Filters>(filters)

  /**
   * Change filters
   */
  const changeFilterHandler: ChangeFilterHandler = useCallback(params => {
    const { requestVar, value, conf, replace = false } = params
    const _filters = draftMode ? draftFilters : filters

    const filtersCopy = produce(_filters, filtersCopy => {
      const isPageRequest = requestVar === CATALOG_PAGE.PAGE
      const isSortRequest = requestVar === CATALOG_SORT.SORT_BY

      if (filtersCopy[requestVar]) {
        const index = filtersCopy[requestVar].indexOf(value[0])
        const currentFilterExists = index >= 0
        if (currentFilterExists && !isPageRequest && !isSortRequest) {
          filtersCopy[requestVar].splice(index, 1)
        } else {
          if (conf?.multiple) {
            filtersCopy[requestVar].push(value[0])
          } else {
            filtersCopy[requestVar] = value
          }
        }
      } else {
        filtersCopy[requestVar] = value
      }

      if (!filtersCopy[requestVar].length) {
        delete filtersCopy[requestVar]
      }

      if (!isPageRequest) {
        filtersCopy[CATALOG_PAGE.PAGE] = ['1']
      }
    })

    if (conf?.draft) {
      setDraftFilters(filtersCopy)
    } else {
      setFilterHandler(filtersCopy, replace)
    }
  }, [setFilterHandler, filters, draftFilters, draftMode])

  /**
   * Remove filter
   */
  const removeFilterHandler: RemoveFilterHandler = useCallback(({ requestVar }: { requestVar: string }) => {
    const newFilters = {
      ...filters
    }
    delete newFilters[requestVar]
    setFilterHandler(newFilters)
  }, [filters, setFilterHandler])

  /**
   * Clear draft filters
   */
  const clearDraftFilters: ClearDraftFiltersHandler = useCallback(() => {
    setDraftFilters({
      ...filters
    })
  }, [filters, setDraftFilters])

  /**
   * Service Query
   */
  const [_pageSize] = filters[CATALOG_PAGE.PAGE_SIZE] ?? []
  const [_currentPage] = filters[CATALOG_PAGE.PAGE] ?? []
  const [sortBy, sortDir] = getCurrentSort() ?? []
  const currentPage = _currentPage ? parseInt(_currentPage) : 1

  const queryParams: VisitureProductsQueryParams = {
    search: searchQuery,
    sort: {
      [sortBy ?? catalogSortDefault]: sortDir ?? CATALOG_SORT.ASC
    },
    pageSize: _pageSize ? parseInt(_pageSize) : defaultPageSize,
    currentPage,
    filter: getFiltersParams(filtersAreAnd, {
      filters,
      categoryId
    })
  }

  const { data: response, isLoading, isFetching, isError } = useQuery(
    ['useProductListPage', queryParams], () => {
      return magentoClient?.catalog.fetchProductListPage(queryParams)
    }, {
      keepPreviousData: true
    }
  )

  /**
   * Attribute Settings
   */
  const { data: attrSettings, isLoading: attrSettingsLoading } = useQuery(
    ['useProductListPage', 'attrSettings'], async () => {
      return fetchAttributeSettings()
    }, {
      enabled: !process.env.REACT_APP_DISABLE_LAYERED_NAV_ATTR_SETTINGS
    })

  /**
   * Products Data
   */
  const visitureProducts = response?.data?.data?.visitureProducts
  const data = useMemo(() => {
    return {
      /**
       * Products
       */
      products: withProducts
        ? visitureProducts?.items.map(product => normalizeProduct({
          product,
          productAttributeList: productAttrList,
          storeConfig
        }))
        : undefined,

      /**
       * Has Items
       */
      hasItems: Boolean(visitureProducts?.items.length),

      /**
       * Page Info
       */
      pageInfo: visitureProducts
        ? normalizePageInfo(visitureProducts.page_info, visitureProducts.total_count)
        : undefined
    }
  }, [visitureProducts, productAttrList, storeConfig, withProducts])

  /**
   * Layered Nav
   */
  const layeredNav = useMemo(() => {
    if (!withLayeredNav || (!attrSettings && !process.env.REACT_APP_DISABLE_LAYERED_NAV_ATTR_SETTINGS)) {
      return
    }

    const _filters = draftMode ? draftFilters : filters
    const filtersCollection = getFiltersCollection(visitureProducts)
    const filtersMap = getFiltersMap(visitureProducts)
    const appliedFilters = getCurrentFilters({
      filtersMap,
      currentFilters: _filters
    })
    const filterOptions = getFilterOptions({
      categoryId,
      filtersMap,
      filtersCollection,
      attrSettings,
      currentFilters: _filters,
      filterItemRenderer: filterItemRenderer.current,
      categoryList: categoryList
    })

    const hasAppliedFilters: boolean = Boolean(appliedFilters.length)

    return {
      categoryId,
      filterOptions,
      appliedFilters,
      hasAppliedFilters
    }
  }, [
    visitureProducts,
    draftFilters,
    filters,
    withLayeredNav,
    attrSettings,
    categoryId,
    categoryList,
    draftMode
  ])

  /**
   * Google Tag Manager (GTM) events.
   */
  useEffect(() => {
    if (!enableGTM || !data.products?.length) {
      return
    }

    const categoryName = categoryId ? categoryList.id[categoryId]?.name : undefined
    if (categoryName) {
      triggerItemList(data.products, categoryName ?? '')
    }

    if (searchQuery) {
      triggerSearch(data.products, searchQuery)
    }
  }, [data, categoryList, categoryId, searchQuery, enableGTM])

  /**
   * InfiniteScroll
   */
  const {
    infiniteScrollRef,
    infiniteScrollProducts,
    infiniteScrollLoading,
    infiniteScrollHasNextPage
  } = useInfiniteScrollAddon({
    currentPage,
    handleFilterChange: changeFilterHandler,
    pageInfo: data.pageInfo,
    loading: isFetching,
    withInfiniteScroll,
    queryParams,
    magentoClient,
    storeConfig,
    productAttributeList: productAttrList,
    products: data.products
  })

  /**
   * Hook API
   */
  return {
    // State
    isLoading: isLoading || attrSettingsLoading || infiniteScrollLoading,
    isFetching,
    isError,

    // Data
    products: withInfiniteScroll ? infiniteScrollProducts : data.products,
    layeredNav,

    // Misc
    filters: draftMode ? draftFilters : filters,
    searchQuery,
    totalCount: visitureProducts?.total_count,
    draftFiltersChanged: draftMode ? !isEqual(draftFilters, filters) : undefined,

    // Infinite Scroll
    infiniteScrollRef,
    infiniteScrollHasNextPage,

    // Sorting and Pagination, TODO: do we need all this?
    sort: visitureProducts?.sort_fields,
    sortOptions: visitureProducts?.sort_fields.options,
    currentSort: getCurrentSort(visitureProducts?.sort_fields),
    pageInfo: data.pageInfo,
    availablePageSizes,
    defaultPageSize,

    // Handlers, TODO: rename?
    handleFilterChange: changeFilterHandler,
    handleFilterRemove: removeFilterHandler,
    clearDraftFilters: clearDraftFilters,
    setFilters: setFilterHandler
  }
}
